/**
 * 학습지 공유를 위한 유틸리티 함수
 */

/**
 * 공유 URL 생성 함수
 * @param {string} worksheetId - 공유할 학습지 ID
 * @param {string} baseUrl - 기본 URL (기본값: 현재 도메인)
 * @returns {string} 공유 URL
 */
export const createShareUrl = (worksheetId, baseUrl = window.location.origin) => {
  if (!worksheetId) return '';
  
  // 학습지 결과 페이지 URL 생성
  return `${baseUrl}/quiz-result/${worksheetId}`;
};

/**
 * URL을 클립보드에 복사하는 함수
 * @param {string} url - 복사할 URL
 * @returns {Promise<boolean>} 성공 여부
 */
export const copyToClipboard = async (url) => {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch (error) {
    console.error('URL 복사 오류:', error);
    
    // fallback: 텍스트 영역 생성하여 복사
    try {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    } catch (fallbackError) {
      console.error('대체 복사 방법 오류:', fallbackError);
      return false;
    }
  }
};

export const confirmTeacherContentShare = (
  confirmFn = window.confirm,
) => confirmFn(
  '링크를 받은 사람은 대화 내용을 볼 수 있어요.\n'
  + '학생 이름, 학교 정보, 개인정보가 없는지 확인해주세요.'
);

const waitForKakaoSdk = async (timeoutMs = 5000) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    if (window.Kakao?.init && window.Kakao?.isInitialized) return window.Kakao;
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  return null;
};

const waitForKakaoShareApi = async (Kakao, timeoutMs = 1000) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    if (Kakao?.Share?.sendDefault) return true;
    await new Promise((resolve) => setTimeout(resolve, 25));
  }
  return false;
};

/**
 * 카카오 JavaScript SDK의 피드 공유를 연다.
 * SDK/키가 준비되지 않으면 원래 공유 흐름이 이어갈 수 있도록 실패 결과만 반환한다.
 */
export const shareToKakao = async ({
  title,
  text,
  url,
  imageUrl = `${window.location.origin}/images/og-image.png`,
  imageWidth,
  imageHeight,
  buttonTitle = '왓퀴즈 보기',
}) => {
  try {
    const appKey = process.env.REACT_APP_KAKAO_APP_KEY;
    if (!appKey) {
      return {
        success: false,
        error: '카카오 JavaScript 키가 설정되지 않았어요.',
        method: 'unavailable',
      };
    }
    const shareUrl = new URL(url, window.location.origin);
    if (!['http:', 'https:'].includes(shareUrl.protocol)) {
      throw new Error('invalid_share_url');
    }

    const Kakao = await waitForKakaoSdk();
    if (!Kakao) {
      return {
        success: false,
        error: '카카오톡 공유를 준비하지 못했어요.',
        method: 'unavailable',
      };
    }
    // HMR·병렬 배포 뒤 이전 키로 초기화된 SDK가 브라우저 전역에 남으면
    // picker가 4011(incorrect app key)을 반환한다. 공유 직전에 현재 build의
    // JavaScript 키로 명시적으로 재초기화한다.
    if (Kakao.isInitialized() && typeof Kakao.cleanup === 'function') {
      Kakao.cleanup();
    }
    Kakao.init(appKey);
    if (!(await waitForKakaoShareApi(Kakao))) {
      throw new Error('카카오톡 공유 모듈을 준비하지 못했어요.');
    }

    const webUrl = shareUrl.toString();
    await Promise.resolve(Kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: String(title || '왓퀴즈').slice(0, 200),
        description: String(text || '').slice(0, 200),
        imageUrl,
        // 카카오 피드 템플릿에 이미지 비율 힌트를 주면 1:1 소재가 잘리지 않는다
        ...(imageWidth && imageHeight ? { imageWidth, imageHeight } : {}),
        link: {
          mobileWebUrl: webUrl,
          webUrl,
        },
      },
      buttons: [{
        title: String(buttonTitle || '왓퀴즈 보기').slice(0, 30),
        link: {
          mobileWebUrl: webUrl,
          webUrl,
        },
      }],
    }));
    return { success: true, error: null, method: 'kakao' };
  } catch (error) {
    return {
      success: false,
      error: error?.message || '카카오톡 공유를 열지 못했어요.',
      method: 'failed',
    };
  }
};

/**
 * Web Share API를 사용하여 콘텐츠 공유
 * @param {Object} shareData - 공유할 데이터 객체
 * @param {string} shareData.title - 공유 제목
 * @param {string} shareData.text - 공유 텍스트
 * @param {string} shareData.url - 공유 URL
 * @returns {Promise<{success: boolean, error: string|null}>} 공유 결과
 */
export const shareContent = async (shareData) => {
  try {
    // Web Share API 지원 여부 확인
    if (!navigator.share) {
      // Web Share API가 지원되지 않는 경우 클립보드에 URL 복사로 대체
      const copySuccess = await copyToClipboard(shareData.url);
      return {
        success: copySuccess,
        error: copySuccess ? null : 'Web Share API를 지원하지 않아 URL 복사에 실패했습니다.',
        method: copySuccess ? 'clipboard' : 'failed',
      };
    }
    
    // Web Share API 호출
    await navigator.share(shareData);
    return {
      success: true,
      error: null,
      method: 'native_share',
    };
  } catch (error) {
    console.error('공유 오류:', error);
    
    // 사용자가 공유를 취소한 경우
    if (error.name === 'AbortError') {
      return {
        success: false,
        error: '사용자가 공유를 취소했습니다.',
        method: 'cancelled',
      };
    }
    
    // 다른 오류 발생 시 클립보드에 복사 시도
    const copySuccess = await copyToClipboard(shareData.url);
    return {
      success: copySuccess,
      error: copySuccess ? '공유에 실패하여 URL을 복사했습니다.' : '공유 및 URL 복사에 실패했습니다.',
      method: copySuccess ? 'clipboard' : 'failed',
    };
  }
}; 